import type { StaffAttentionSummary } from "@lafayette/schemas";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { type ConsoleFeatures, NO_CONSOLE_FEATURES } from "../navigation/offering";
import { type HomeRuntime, useHomeRuntime, useOptionalHomeRuntime } from "./runtime";

// The one read behind Home and the shell: `GET /staff/attention`. The shell (the Home badge and the hidden Payments entry) and the Home
// page share this query key, so one request serves both. The answer holds customer display names, so it stays in memory only while a
// screen uses it (`gcTime: 0`) and is never written to browser storage. The list is read again every minute while the tab is visible and
// when the tab gets focus. A failed read is not retried by the library: Home shows the error and offers a retry; the shell shows nothing
// extra (no badge, no hidden entry).

const attentionKeys = {
  summary: ["staff-attention"] as const,
  displayName: ["home-staff-display-name"] as const,
};

const ATTENTION_REFRESH_MS = 60_000;

export function useAttentionSummary(enabled = true) {
  const { api } = useHomeRuntime();
  return useQuery<StaffAttentionSummary>({
    queryKey: attentionKeys.summary,
    queryFn: ({ signal }) => api.summary({ signal }),
    enabled,
    gcTime: 0,
    staleTime: 30_000,
    retry: false,
    // A failed read stays failed until the person retries (or the next timed read). Without this, the page that mounts after the shell reads
    // again and the error flashes away; with a down API that is a request on every navigation.
    retryOnMount: false,
    refetchInterval: ATTENTION_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * The display name of the signed-in staff member, for the greeting (`GET /staff/me` through the Home runtime). It is the person's own name,
 * kept in memory only. A runtime with no reader, a pending read and a failed read all give `null`: the greeting then has no name. A failed
 * read is not retried; the greeting is not worth a second request.
 */
export function useStaffDisplayName(): string | null {
  const runtime = useOptionalHomeRuntime();
  const reader = runtime?.displayName;
  const query = useQuery<string>({
    queryKey: attentionKeys.displayName,
    queryFn: ({ signal }) => (reader as NonNullable<typeof reader>)({ signal }),
    enabled: reader !== undefined,
    gcTime: 0,
    staleTime: 5 * 60_000,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
  return query.data ?? null;
}

/**
 * What needs the staff member now: the sources that need action (not the information ones), counted. `partial` is true when the number is a
 * lower bound: a source could not be read, or a count stopped at its cap. The badge then shows "N+", so a blind queue never looks empty.
 */
export function actionableCount(summary: StaffAttentionSummary | undefined): {
  count: number;
  partial: boolean;
} {
  if (summary === undefined) return { count: 0, partial: false };
  const actionable = summary.sources.filter((source) => source.severity !== "info");
  return {
    count: actionable.reduce((total, source) => total + source.count, 0),
    partial: actionable.some((source) => source.status === "unavailable" || source.capped),
  };
}

/**
 * The features the API switched on, for the offering (`useOffering`: the shell, the guard, the Home tiles and every link between areas).
 * It shares the query of the shell, so it adds no request. Without a Home runtime, while the read is pending and after a failed read every
 * feature is off (fail closed: a cached answer is not trusted after a failure). `isLoading` is true only while a runtime waits for its
 * first answer.
 */
export function useConsoleFeatures(): Readonly<{ features: ConsoleFeatures; isLoading: boolean }> {
  const runtime = useOptionalHomeRuntime();
  const query = useQuery<StaffAttentionSummary>({
    queryKey: attentionKeys.summary,
    queryFn: ({ signal }) => (runtime as HomeRuntime).api.summary({ signal }),
    enabled: runtime !== null,
    gcTime: 0,
    staleTime: 30_000,
    retry: false,
    retryOnMount: false,
  });
  const payments = runtime !== null && !query.isError && query.data?.features.payments === true;
  const features = useMemo<ConsoleFeatures>(
    () => (payments ? { payments } : NO_CONSOLE_FEATURES),
    [payments],
  );
  return { features, isLoading: runtime !== null && query.isPending };
}
